import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Mutation, Query } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context, publishFilter } from '@adornis/baseql/server/context.js';
import { selectionSet, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { getExamDayFilter } from '@ils/permissions/examDayFilter.js';
import { hasPermission } from '@ils/permissions/hasPermission.js';
import { Names } from '@ils/static/names.js';
import { DateTime } from 'luxon';
import { randomBytes } from 'node:crypto';
import { ExamDay } from './examdays.js';
import { ILSUser } from './ils-user.js';

@Entity()
export class ILSToken extends MongoEntity {
  public static override _class = Names.ILSToken._class;
  public static override _collectionName = Names.ILSToken._collectionName;

  static override get allFields() {
    return selectionSet(() => ILSToken, 1);
  }

  @Field(type => String)
  public loginToken!: string;
  @Field(type => String)
  public shortToken!: string;
  @Field(type => String)
  public userID!: string;
  @Field(type => String)
  public examDayID!: string;
  @Field(type => Date)
  public createdAt!: Date;

  @Mutation(type => [ILSToken])
  public static generateShortTokensForUsers(
    @Arg('userIDs', type => [String]) userIDs: string[],
    @Arg('examDayID', type => String) examDayID: string,
  ) {
    return async (gqlFields: BaseQLSelectionSet<ILSToken>) => {
      if (context.serverContext || !context.userID) throw new Error('you are not authorized to create shortTokens');
      await assertTokenCreatePermission(examDayID);

      const day = await getByID(ExamDay, examDayID)({});
      if (!day) throw new Error('requested examDay does not exist');
      const now = DateTime.fromJSDate(new Date());
      await this.cleanupOldTokens(now);
      const activeTokens = (await this.getActiveTokens(now)).map(token => token.shortToken);
      const users = (await ILSUser.getByID<ILSUser>(userIDs)({ _id: 1 })).sort(
        (a, b) => userIDs.indexOf(a._id) - userIDs.indexOf(b._id),
      );
      const tokens: ILSToken[] = [];
      for (let i = 0; i < users.length; i++) {
        let shortToken = '';
        do {
          shortToken = Array.from(randomBytes(4))
            .map(num => ('0' + num.toString(16)).substr(-2))
            .join('');
        } while (activeTokens.includes(shortToken));
        activeTokens.push(shortToken);
        // ttl of 24h
        const loginToken = await generateLoginToken({
          userID: users[i]!._id,
          userClass: ILSUser._class,
          ttu: -1,
          ttl: 1000 * 60 * 60 * 24,
        });
        const token = new ILSToken({
          loginToken,
          shortToken,
          userID: userIDs[i],
          createdAt: now.toJSDate(),
          examDayID,
        });
        await token.create();
        tokens.push(token);
      }
      return tokens;
    };
  }

  @Query(type => [ILSToken], { requestHandlerMeta: { queryCache: { cachePolicy: 'bypass' } } })
  public static getShortTokenByUserID(@Arg('userIDs', type => [String]) userIDs: string[]) {
    return async (gqlFields: BaseQLSelectionSet<ILSToken>) => {
      if (context.serverContext || !context.userID) throw new Error('you are not authorized to view shortTokens');
      const user = await CurrentUserInfo.getMyself<ILSUser>()({ _id: 1, roles: { name: 1, contexts: 1 } });
      if (!hasPermission(user, 'shortTokens', 'view')) throw new Error('you are not authorized to view shortTokens');

      return this.getShortTokensByUserLogic(userIDs);
    };
  }

  static async getShortTokensByUserLogic(userIDs: string[]) {
    if (!A.isServer) throw new Error('Call this only on the server.');
    const collection = await getRawCollection<EntityData<ILSToken>>(this._collectionName);
    const time = DateTime.now();
    const validStart = time.minus({ hours: 24 });
    return (
      await collection
        .find(
          {
            _class: this._class,
            userID: { $in: userIDs },
            $and: [{ createdAt: { $gt: validStart.toJSDate() } }, { createdAt: { $lt: time.toJSDate() } }],
          },
          { sort: { createdAt: -1 }, limit: userIDs.length },
        )
        .toArray()
    ).map(t => new this(t));
  }

  private static async getActiveTokens(time: DateTime) {
    const validStart = time.minus({ hours: 24 });
    const collection = await getRawCollection<EntityData<ILSToken>>(this._collectionName);
    return collection
      .find({
        $and: [
          { _class: this._class, createdAt: { $gt: validStart.toJSDate() } },
          { createdAt: { $lt: time.toJSDate() } },
        ],
      })
      .toArray();
  }

  private static async cleanupOldTokens(time: DateTime) {
    const validStart = time.minus({ hours: 24 });
    const collection = await getRawCollection(this._collectionName);
    return collection.deleteMany({ _class: this._class, createdAt: { $lt: validStart.toJSDate() } });
  }
}

@Entity()
export class ExamToken extends MongoEntity {
  public static override _class = Names.ExamToken._class;
  public static override _collectionName = Names.ExamToken._collectionName;

  @Field(type => ID)
  public examDayID!: string;

  @Field(type => String)
  public shortToken!: string;

  @Field(type => Date, { default: v => v ?? new Date() })
  public createdAt!: DateTime;

  @Mutation(type => ExamToken)
  public static generateExamToken(@Arg('examDayID', type => ID) examDayID: string) {
    return async (gqlFields: BaseQLSelectionSet<ExamToken>) => {
      if (context.serverContext || !context.userID) throw new Error('you are not authorized to create shortTokens');
      await assertTokenCreatePermission(examDayID);

      const day = await getByID(ExamDay, examDayID)({});
      if (!day) throw new Error('Der angegebene ExamDay konnte nicht gefunden werden.');
      const now = DateTime.fromJSDate(new Date());
      await this.cleanupOldTokens(examDayID, now);
      const shortToken = Array.from(randomBytes(4))
        .map(num => ('0' + num.toString(16)).substr(-2))
        .join('');
      const examToken = new ExamToken({ examDayID, shortToken, createdAt: now });
      await examToken.create();
      return examToken;
    };
  }

  @Query(type => ExamToken, { requestHandlerMeta: { queryCache: { cachePolicy: 'bypass' } } })
  public static getTokenForExamDay(@Arg('examDayID', type => ID) examDayID: string) {
    return async (gqlFields: BaseQLSelectionSet<ExamToken>) => {
      if (context.serverContext || !context.userID) throw new Error('you are not authorized to access these tokens');
      const user = await CurrentUserInfo.getMyself<ILSUser>()({ _id: 1, roles: { name: 1, contexts: 1 } });
      if (!hasPermission(user, 'shortTokens', 'view')) throw new Error('you are not authorized to view shortTokens');

      const tokenPublishFilter = await publishFilter(this._class);
      if (!tokenPublishFilter) throw new Error('no publishfilter defined for ExamToken, please define one');
      const collection = await getRawCollection<EntityData<ExamToken>>(this._collectionName);
      const now = DateTime.now();
      const validStart = now.minus({ hours: 24 });
      const examToken = await collection
        .find(
          {
            $and: [
              tokenPublishFilter,
              {
                _class: this._class,
                examDayID,
                createdAt: { $gt: validStart.toJSDate() },
              },
            ],
          },
          { sort: { createdAt: -1 } },
        )
        .toArray();
      if (!examToken[0]) {
        logger.info('Für diesen ExamDay wurde noch kein Token generiert.');
        return undefined;
      }
      return new ExamToken(examToken[0]);
    };
  }

  private static async cleanupOldTokens(examDayID: string, time: DateTime) {
    if (!A.isServer) throw new Error('Only call this from server!');

    const validStart = time.minus({ hours: 24 });
    const collection = await getRawCollection(this._collectionName);
    return collection.deleteMany({ _class: this._class, examDayID, createdAt: { $lt: validStart } });
  }
}

async function assertTokenCreatePermission(examDayID: string) {
  const user = await CurrentUserInfo.getMyself<ILSUser>()({ _id: 1, roles: { name: 1, contexts: 1 } });
  const examDayFilter = getExamDayFilter(user);
  const allowedExamDayIDs = (
    await (await getRawCollection<ExamDay>(Names.ExamDay._collectionName))
      .find(examDayFilter, { projection: { _id: 1 } })
      .toArray()
  ).map(x => x._id);
  if (!hasPermission(user, 'shortTokens', 'create', { _meta: { allowedExamDayIDs }, examDayID, _id: '__ALL__' }))
    throw new Error('you are not authorized to access these tokens');
}
